/**
 * Properties menu: clickable list of B2B, User or Event properties and cohorts to go inside mp-drop-menu
 */
import compact from 'lodash/compact';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import intersectionBy from 'lodash/intersectionBy';
import partition from 'lodash/partition';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';
import sortBy from 'lodash/sortBy';

import {baseComparator} from 'common/util/array';
import {
  SPECIAL_EVENTS,
  SPECIAL_CONTROLS,
  STRING_TYPECAST_PROPERTIES,
  TYPECAST_PROPERTY_TYPES,
} from '../../../report/constants';
import eventPropertiesClient from '../../../report/queries/query-api/event-properties-client';
import peoplePropertiesClient from '../../../report/queries/query-api/people-properties-client';
import cohortStore from '../../../report/stores/cohorts';
import userDetailsStore from 'common/report/stores/user-details';
import customPropertiesStore from '../../../report/stores/custom-properties';
import peoplePropertiesStore from '../../../report/stores/people-properties';
import eventPropertiesStore from '../../../report/stores/event-properties';
import {
  eventPropertyDefinitionsStore,
  peoplePropertyDefinitionsStore,
} from '../../../report/stores/property-definitions';
import {Store} from '../../../report/stores/store';
import {getIconForPropertyType, getLabelForPropertyType} from '../../../report/util';
import {PropertyDefinition} from '../../../util/definitions-types';
import {registerMPElement} from '../../../util/register-element';
import {DataGroup} from '../../../widgets/data-group-selector/types';
import {PropertyType, ResourceType} from '../../../widgets/property-filter-menu/models/types';
import {isPersistedCustomProperty, getInlineCustomPropertyId} from '../../../widgets/custom-property-modal/util';
import {TooltipData} from '../../context-panel/types';
import {getTooltipDataForCohort, getTooltipDataForProperty} from '../../context-panel/util';
import {TimeUnit, SpecialTimeUnit, TimeUnitLabels} from 'common/widgets/date-range-picker/models/types';
import {INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING} from 'common/widgets/property-filter-menu/models/insights-filter';
import StagedCohort from 'common/widgets/cohorts-query-builder/models/builder/staged-cohort';
import CohortModel from 'common/widgets/cohorts-query-builder/models/cohort';
import {DISABLED_DUE_TO_SENSITIVE_DATA} from 'common/report/constants';
import {
  SerializedCohort,
  SerializedInlineCohort,
  isSerializedInlineCohort,
} from 'common/widgets/cohorts-query-builder/models/types/serialized-cohort-types';
import {ChildMenu} from '../child-menu';
import {ALL_USERS_COHORT, OTHER_OPTIONS} from './constants';
import {
  PropertiesMenu,
  PropertyCombineMode,
  PropertyItem,
  PropertyItemKeys,
  PropertyItemPersistedIdentifierKeys,
  PropertyItemAction,
  SelectedTab,
} from './types';
import {TrackingEvent} from './tracking';
import {calculatePropertiesIntersection, storeCustomPropertyToProperty, sanitizeCustomProperty} from './util';

import template from './index.jade';
import css from './index.styl?inline';
import {dataGroupIDsEqual} from 'common/util';

// number of items to show per section on the "All" tab
// remaining items are hidden under a `Show all` expander
const MENU_ITEM_LIMIT = 10;

const PropertyDefinitionStoreMap = {
  [ResourceType.User]: peoplePropertyDefinitionsStore,
  [ResourceType.Event]: eventPropertyDefinitionsStore,
};

enum StagedMenuTypes {
  Typecast,
  Dimension,
  Datetime,
}

const DefaultToggleTab = `all`;

enum EventPropertySource {
  Empty,
  Intersection,
  Store,
}

enum MODAL_TYPE {
  InlineCohort,
  CustomProperty,
}

type DataGroupItem = DataGroup & {
  properties: any[];
  isDimension: boolean;
  isLoading: boolean;
};

interface PropertiesMenuState {
  dataGroups: {[id: string]: DataGroupItem};
  eventProperties: any[];
  lastMultiSelection: PropertyItem[];
  loadingCohorts: boolean;
  loadingEventProperties: boolean;
  loadingPeopleProperties: boolean;
  openModal: MODAL_TYPE | null;
  selectedTab: SelectedTab;
  stagedCohort: StagedCohort;
  stagedProperty: PropertyItem;
  stagedMenuType: StagedMenuTypes | null;
  tooltipData: TooltipData;
  stagedSearchFilter: string;
  stagedCustomProperty: any;
}

export default registerMPElement(
  `mp-properties-menu`,
  class extends ChildMenu<PropertiesMenuState> implements PropertiesMenu {
    private _eventPropertySource;
    private _useMultipleSelection = false;
    private _context = null;
    private _cohortStoreCallback = null;
    private _customPropertiesStoreCallback = null;
    private _eventPropertiesStoreCallback = null;
    private _eventPropertiesStoreKey = null;
    private _peoplePropertiesStoreCallback = null;

    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        'cohort-operation-type': `string`,
        'allow-typecast': {
          type: `boolean`,
          description: `allows typecasting for property selection`,
        },
        'disabled-items': {
          type: `json`,
          default: {}, // dict for fast inclusion lookups
          description: `sets disabled options for the underlying mp-items-menu`,
        },
        'enable-behavioral-properties': {
          type: `boolean`,
          default: false,
        },
        'include-custom-properties': {
          type: `string`,
          enum: [`none`, `persisted-only`, `all`],
          default: `none`,
          description: `whether to hide custom properties from properties menus`,
        },
        'exclude-dimension-properties': {
          type: `boolean`,
          default: false,
          description: `whether to hide dimension properties from properties menus`,
        },
        'property-combine-mode': {
          type: `string`,
          default: PropertyCombineMode.Union,
          description: `defines how event properties should be merged`,
          enum: Object.values(PropertyCombineMode),
        },
        'show-all-tab': {
          type: `boolean`,
          description: `enables the "All" tab in the selection menu when there is more than one section`,
        },
        'show-all-users-cohort': {
          type: `boolean`,
          description: `enables the "All Users" cohort if cohort selection is enabled`,
        },
        'show-items-caret': {
          type: `boolean`,
          description: `enables the a caret icon in the selection items`,
        },
        'hide-edit-controls': {
          type: `boolean`,
          description: `show controls that popup modals to edit entities like custom events and properties`,
        },
        'single-data-group': {
          type: `string`,
          description: `if set, shows group properties for a specific data group id`,
        },
        'other-options': {
          type: `json`,
          default: [],
          description: `list of items to show in the other section`,
        },
      };
    }

    get config() {
      return {
        css,
        template,
        useShadowDom: true,

        defaultState: {
          dataGroups: {},
          eventProperties: [],
          isLoading: false,
          loadingCohorts: false,
          loadingEventProperties: false,
          loadingPeopleProperties: false,
          openModal: null,
          selectedTab: DefaultToggleTab,
          stagedCohort: new StagedCohort(),
          stagedProperty: null,
          stagedMenuType: null,
          lastMultiSelection: null,
          tooltipData: null,
          stagedSearchFilter: ``,
          stagedCustomProperty: {},
        },

        helpers: {
          ...super.config.helpers,
          MODAL_TYPE,
          StagedMenuTypes,
          getMenuSections: () => this.getMenuSections(),
          getToggleTabs: () => this.getToggleTabs(),
          onItemClick: (ev) => this.onItemClick(ev.detail.item),
          onItemPillClick: (ev) => this.handleItemPillClick(ev.detail.item),
          applyStagedValue: (ev) => this.applyStagedValue(ev.detail.item),
          unstageProperty: () => this.updateStagedProperty(null, null),
          showItemDetails: debounce((ev) => this.showItemDetails(ev.detail.item), 100),
          useMultipleSelection: () => this.isMultipleSelection(),
          isInnerContentSearchable: () => this.isInnerContentSearchable(),
          getDefaultFormulaName: () => {
            const entityNames = this.getCustomEntities([ResourceType.Event, ResourceType.User]).map(
              (item) => item.value,
            );
            const prefix = this.attr(`cohort-operation-type`)
              ? `Custom ${this.attr(`cohort-operation-type`)}`
              : `Custom formula`;
            for (let i = 1; ; i++) {
              const name = `${prefix} ${i}`;
              if (!entityNames.includes(name)) {
                return name;
              }
            }
          },

          handleTooltipContextAction: (ev) => {
            const {name, item} = ev.detail;

            if (name === `editCustomProperty`) {
              this.clickEditCustomProperty(item.property);
            } else if (name === `editCohort` && item.value && item.value.cohort && item.value.cohort.raw_cohort) {
              // Composed events can cross an open shadow boundary without bubbles: true
              ev.stopPropagation();
              this.editCohort(item);
            } else {
              this.helpers.propagateEvent(ev);
            }
          },

          handleSearchInput: (ev) => {
            ev.stopPropagation();
            this.update({stagedSearchFilter: ev.target.value});
          },

          applyModalState: (type, state) => {
            switch (state) {
              case `open`:
                this.update({openModal: type});
                break;
              case `closed`:
                this.update({openModal: null});
                break;
            }
          },
          updateStagedCohort: (ev) => this.update({stagedCohort: ev.detail.stagedCohort}),
          commitStagedCohort: (ev) => this.commitStagedCohort({name: ev.detail ? ev.detail.name : ``}),
          commitSavedCohort: (ev) => this.commitStagedCohort(ev.detail, {isSaved: true}),

          selectOption: (ev) => {
            const {selected} = ev.detail;

            this.update({selectedTab: selected});
            this.trackEvent(TrackingEvent.SelectPropertiesDropdownOption, {
              context: this._context,
              selectedTab: selected,
            });
            this.dispatchEvent(new CustomEvent(`selectoption`, {detail: {selected}}));
          },

          getStagedMenuHeader: () => {
            const {stagedProperty, stagedMenuType} = this.state;

            switch (stagedMenuType) {
              case StagedMenuTypes.Typecast:
                return `${stagedProperty.label} Typecast`;
              case StagedMenuTypes.Datetime:
                return `Date Time Unit`;
              case StagedMenuTypes.Dimension:
                return `${stagedProperty.label}`;
              default:
                return null;
            }
          },

          getStagedMenuSections: () => {
            const {stagedMenuType} = this.state;

            switch (stagedMenuType) {
              case StagedMenuTypes.Typecast:
                return this.helpers.getTypecastMenuSections();
              case StagedMenuTypes.Datetime:
                return this.helpers.getDatetimeMenuSections();
              case StagedMenuTypes.Dimension:
                return this.helpers.getDimensionMenuSection();
            }
          },

          getDimensionMenuSection: () => {
            const {dataGroupId, resourceType, value: dimension} = this.state.stagedProperty;

            if (!this.state.dataGroups[dataGroupId]) {
              // Fetch results in background
              this.fetchDataGroupResourceProperties(resourceType, dataGroupId, [dimension]);

              return [
                {
                  isLoading: true,
                  items: [],
                },
              ];
            } else if (this.state.dataGroups[dataGroupId].isLoading) {
              return [
                {
                  isLoading: true,
                  items: [],
                },
              ];
            }

            return [
              {
                items: this.getPropertyMenuItems({
                  properties: this.state.dataGroups[dataGroupId].properties,
                  resourceType,
                }),
              },
            ];
          },

          getDatetimeMenuSections: () => {
            const timeUnits = Object.values(TimeUnit).filter((unit) => unit !== TimeUnit.Minute);
            const units = [...timeUnits, ...Object.values(SpecialTimeUnit)];

            const items = units.map((value) => {
              return {
                label: TimeUnitLabels[value],
                value,
              };
            });

            return [{items}];
          },

          getTypecastMenuSections: () => {
            const typeCastTypes = Object.values(TYPECAST_PROPERTY_TYPES);
            const {type} = this.state.stagedProperty;
            const types = [...typeCastTypes, ...(typeCastTypes.includes(type) ? [] : [type])];

            const items = types.map((typeCastType) => {
              return {
                icon: getIconForPropertyType(typeCastType),
                isSelected: typeCastType === type,
                label: getLabelForPropertyType(typeCastType),
                value: typeCastType,
              };
            });

            return [{items}];
          },

          sectionFromInput: (input, resourceType) => {
            if (resourceType === ResourceType.Event || resourceType === ResourceType.User) {
              return {
                items: [
                  {
                    type: PropertyType.String,
                    propertyDefaultType: PropertyType.String,
                    label: input,
                    value: input,
                    resourceType,
                    icon: `plus`,
                  },
                ],
              };
            }
            return null;
          },

          //#region Custom properties
          closeCustomPropertyModal: () => {
            this.update({
              openModal: null,
              stagedCustomProperty: {},
              tooltipData: null,
            });
          },
          deletedCustomProperty: () => {
            const selectedCustomPropertyId = (this.attr(`selected`) || {}).customPropertyId;
            // when filter menu is open, `selected` on the underlying
            // properties-menu is set to null, so we have to manually delete
            // ourselves here. the delete callback can handle anything else.
            if (!selectedCustomPropertyId) {
              this.dispatchEvent(new CustomEvent(`deletecustomproperty`));
            }
            this.helpers.closeCustomPropertyModal();
          },
          applyCustomProperty: (ev) => {
            let {property} = ev.detail;
            let {resourceType, customProperty, customPropertyId} = property;
            const selectedCustomPropertyId = (this.attr(`selected`) || {}).customPropertyId;
            if (
              !selectedCustomPropertyId ||
              (selectedCustomPropertyId !== customPropertyId &&
                (isPersistedCustomProperty(selectedCustomPropertyId) ||
                  customPropertiesStore.value[selectedCustomPropertyId].newCustomPropertyId !== customPropertyId))
            ) {
              // we only deal with the 'create' case and the 'update' case for the opened filter
              // menu since it doesn't have a `selected` set

              // we use 'user' and 'event' in the new query builder
              if (Object.keys(INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING).includes(resourceType)) {
                resourceType = INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING[resourceType];
              }
              customProperty = sanitizeCustomProperty(customProperty);
              if (isPersistedCustomProperty(customPropertyId)) {
                customPropertyId = Number(customPropertyId);
              }
              property = {
                ...property,
                customProperty,
                customPropertyId,
                resourceType,
              };
              this.selectItem(property, property);
            }
            this.helpers.closeCustomPropertyModal();
          },
          //#endregion
        },
      };
    }

    customPropertiesStoreCallback() {
      const selectedCustomPropertyId = (this.attr(`selected`) || {}).customPropertyId;
      if (selectedCustomPropertyId in customPropertiesStore.value) {
        const newCustomPropertyId = customPropertiesStore.value[selectedCustomPropertyId].newCustomPropertyId;

        const property = storeCustomPropertyToProperty(newCustomPropertyId || selectedCustomPropertyId);

        const selectedCustomProperty = sanitizeCustomProperty((this.attr(`selected`) || {}).customProperty);

        if (newCustomPropertyId || !isEqual(property.customProperty, selectedCustomProperty)) {
          // update for breakdown menu and filter menus that are closed (the opened one has no `selected` set)
          const item = this.getPropertyMenuItem({
            property,
            resourceType: property.resourceType,
          });
          this.selectItem(item, item, false, {}, true);
        }
      } else if (
        isPersistedCustomProperty(selectedCustomPropertyId) &&
        Object.keys(customPropertiesStore.value).length > 0
      ) {
        // delete
        this.dispatchEvent(new CustomEvent(`deletecustomproperty`));
      }
    }

    public async connectedCallback() {
      super.connectedCallback();

      this.fetchData();
      this.updateFromSelection();

      this._customPropertiesStoreCallback = () => this.customPropertiesStoreCallback();

      customPropertiesStore.subscribe(this._customPropertiesStoreCallback);
      await customPropertiesStore.fetch({
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
      });

      this.loadStoreValues();
    }

    public disconnectedCallback() {
      customPropertiesStore.unsubscribe(this._customPropertiesStoreCallback);
      eventPropertiesStore.unsubscribe(this._eventPropertiesStoreCallback, this._eventPropertiesStoreKey);
      peoplePropertiesStore.unsubscribe(this._peoplePropertiesStoreCallback);

      if (this.attr(`project-has-cohorts`)) {
        const dataGroupId = this.attr(`data-group-id`);
        if (dataGroupId && this.attr(`support-b2b-cohorts`)) {
          cohortStore.unsubscribe(this._cohortStoreCallback, dataGroupId);
        } else {
          cohortStore.unsubscribe(this._cohortStoreCallback);
        }
      }

      super.disconnectedCallback();
    }

    public loadStoreValues() {
      // all properties-menus upload any inline custom properties they host.
      const inlineCustomProperties = flatten(
        this.getPropertyResourceTypes().map((type) => {
          return this.getSelectedCustomFormula(type);
        }),
      ).reduce((acc, cur) => {
        const {customPropertyId, customProperty} = cur;
        return {
          ...acc,
          [getInlineCustomPropertyId(customPropertyId)]: customProperty,
        };
      }, {});

      customPropertiesStore.update(inlineCustomProperties);

      // load all inline cohorts into cohort store
      cohortStore.update(
        this.getInlineCohortItems().reduce((acc, cur: PropertyItem) => {
          return {
            ...acc,
            [cur.value.cohort.raw_cohort.unsavedId]: {
              name: cur.label,
              created_by: cur.createdBy,
              raw_cohort: cur.value.cohort.raw_cohort,
            } as SerializedInlineCohort,
          };
        }, {}),
      );
    }

    public isContentSearchable() {
      return !this.state.stagedProperty;
    }

    public isInnerContentSearchable() {
      return this.state.stagedProperty && this.state.stagedMenuType === StagedMenuTypes.Dimension;
    }

    /**
     * Sets the active tab to the section that the currently
     * selected value belongs to, defaults to the "All" tab
     * when enabled or to the first tab otherwise
     */
    public updateFromSelection() {
      const selected = this.attr(`selected`);
      const isCohort = Array.isArray(selected);
      const tabs = this.getToggleTabs();

      if (selected && (isCohort || selected.dataGroupId || selected.resourceType)) {
        this.update({selectedTab: isCohort ? ResourceType.Cohort : selected.dataGroupId || selected.resourceType});
      } else if (tabs.length && !tabs.find((tab) => tab.label === `All`)) {
        this.update({selectedTab: tabs[0].value});
      } else {
        this.update({selectedTab: DefaultToggleTab});
      }
    }

    public attributeChangedCallback(attr, oldVal, newVal) {
      super.attributeChangedCallback(attr, oldVal, newVal);

      // only update properties if project-id has already been defined
      if (!this.initialized || !this.attr(`project-id`)) {
        return;
      }

      switch (attr) {
        case `all-data-groups`:
        case `data-group-id`:
        case `event-names`:
        case `include-custom-properties`:
        case `project-has-cohorts`:
        case `project-has-events`:
        case `project-has-people`:
        case `project-id`:
        case `property-combine-mode`:
        case `single-data-group`:
          this.fetchData();
          this.updateFromSelection();
          break;
        case `selected`:
          this.loadStoreValues();
          if (!this.attr(`open`)) {
            this.updateFromSelection();
          }
          break;
        case `open`:
          if (this.attr(`open`)) {
            // stages lastMultiSelection from initial selected state
            const selected = this.getArraySelection();
            this.update({lastMultiSelection: Array.from(selected)});
          } else {
            this.update({
              stagedProperty: null,
              tooltipData: null,
            });
          }
          break;
        default:
          break;
      }

      this.customPropertiesStoreCallback();
    }

    public getSelectedTab() {
      return this.state.selectedTab;
    }

    /**
     * Defines if the selection menu allows multiple selection, either by props or attributes
     */
    public isMultipleSelection() {
      return this._useMultipleSelection || this.attr(`multiple`);
    }

    public selectedLabel() {
      return [{label: this.getSelectedPropertyName() || `Select a property`, type: `primary`}];
    }

    public getItemUniqueKey(item) {
      return item[PropertyItemPersistedIdentifierKeys.find((key) => !!item[key])];
    }

    public getPersistenceKey() {
      return `properties-menu`;
    }

    private clickEditCustomProperty(property) {
      this.update({
        openModal: MODAL_TYPE.CustomProperty,
        stagedCustomProperty: {property},
      });
    }

    private handleItemPillClick(item) {
      if (item.pillAction === PropertyItemAction.Typecast) {
        this.updateStagedProperty(item, StagedMenuTypes.Typecast);
      } else {
        console.warn(`Invalid primary action for property item`);
      }
    }

    private updateStagedProperty(stagedProperty, stagedMenuType) {
      this.update({stagedProperty, stagedMenuType, stagedSearchFilter: ``});
      this.dispatchEvent(new CustomEvent(`stagedproperty`, {detail: {item: stagedProperty}}));
    }

    private onItemClick(item) {
      const rawItem = item;
      item = pick(item, PropertyItemKeys);

      if (rawItem.value === SPECIAL_CONTROLS.CREATE_INLINE_COHORT.value) {
        return this.createCohort();
      } else if (
        rawItem.label === SPECIAL_CONTROLS.CREATE_CUSTOM_EVENT_FORMULA.label ||
        rawItem.label === SPECIAL_CONTROLS.CREATE_CUSTOM_PEOPLE_FORMULA.label
      ) {
        const props = pick(item, [`resourceType`]);
        const stagedCustomProperty = {property: props};
        this.update({
          openModal: MODAL_TYPE.CustomProperty,
          stagedCustomProperty,
        });
        this.trackEvent(TrackingEvent.CreateCustomProperty, {
          context: this._context,
          name: item.name,
          ...props,
        });
      } else {
        this.selectItem(item, rawItem, true);
      }
    }

    private addSelectedItemToRecent(item) {
      // Don't store inline cohorts or unsaved custom properties for now
      if (
        (item.value && item.value.cohort && item.value.cohort.raw_cohort) ||
        (item.customProperty && !isPersistedCustomProperty(item.customPropertyId))
      ) {
        return;
      }

      this.addRecentItem(item.resourceType, pick(item, PropertyItemPersistedIdentifierKeys));
    }

    private selectItem(item, rawItem = null, onItemClicked = false, {commit = false} = {}, update = false) {
      // Pickout undefined values to prevent duplicate actions triggering
      let selection = pickBy(item, (value) => value !== undefined);

      if (
        this.attr(`show-special-date-property`) &&
        !item.unit &&
        item.propertyDefaultType === PropertyType.Datetime &&
        this.attr(`show-special-date-property-unit`) &&
        !update
      ) {
        return this.updateStagedProperty(rawItem, StagedMenuTypes.Datetime);
      }

      if (item.type === PropertyType.Dimension) {
        return this.updateStagedProperty(rawItem, StagedMenuTypes.Dimension);
      }

      if (this.isMultipleSelection()) {
        // if item was not removed, add it to the selection
        const baseSelection = this.getArraySelection();
        selection = baseSelection.filter((selectedItem) =>
          selectedItem.value &&
          isSerializedInlineCohort(selectedItem.value.cohort) &&
          isSerializedInlineCohort(item.value.cohort)
            ? !isEqual(selectedItem.value.cohort.raw_cohort, item.value.cohort.raw_cohort)
            : selectedItem.value !== item.value,
        );
        if (baseSelection.length === selection.length) {
          selection.push(pick(item, PropertyItemKeys));
        }
      }

      this.addSelectedItemToRecent(item);
      this.dispatchEvent(new CustomEvent(`select`, {detail: {item: selection, rawItem, onItemClicked, commit}}));

      if (onItemClicked) {
        this.trackSelection({
          eventName: TrackingEvent.SelectProperty,
          props: {},
        });
      }
    }

    private replaceItem(item, index, rawItem = null, onItemClicked = false, {commit = false} = {}) {
      // Pickout undefined values to prevent duplicate actions triggering
      let selection = pickBy(item, (value) => value !== undefined);

      if (this.isMultipleSelection()) {
        // if item was not removed, add it to the selection
        const baseSelection = this.getArraySelection();
        baseSelection[index] = pick(item, PropertyItemKeys);

        selection = baseSelection;
      }

      this.addSelectedItemToRecent(item);
      this.dispatchEvent(new CustomEvent(`select`, {detail: {item: selection, rawItem, commit}}));

      if (onItemClicked) {
        this.trackSelection({
          eventName: TrackingEvent.SelectProperty,
          props: {},
        });
      }
    }

    /**
     * Appends the selected type to the staged property
     */
    private applyStagedValue(item) {
      const {stagedProperty, stagedMenuType} = this.state;

      this.update({stagedProperty: null});

      if (stagedMenuType === StagedMenuTypes.Typecast) {
        this.onItemClick({
          ...stagedProperty,
          type: item.value,
        });
      } else if (stagedMenuType === StagedMenuTypes.Datetime) {
        this.onItemClick({
          ...stagedProperty,
          unit: item.value,
        });
      } else if (stagedMenuType === StagedMenuTypes.Dimension) {
        this.onItemClick({
          ...stagedProperty,
          value: item.value,
          type: item.type,
          propertyDefaultType: item.type,
          dataGroupId: null, // We don't need to include dataGroupId for resolved dimensions
        });
      } else {
        throw new Error(`Invalid staged menu type ${stagedMenuType}`);
      }
    }

    private fetchData() {
      this.fetchPeopleProperties();
      this.fetchEventProperties();
      this.fetchDataGroups();
      this.fetchCohorts();
      this.fetchUserDetails();
      this.fetchPropertyDefinitions();
    }

    private getPropertyResourceTypes() {
      return compact([
        this.attr(`project-has-people`) ? ResourceType.User : null,
        this.attr(`project-has-events`) ? ResourceType.Event : null,
      ]);
    }

    private fetchPropertyDefinitions() {
      for (const type of this.getPropertyResourceTypes()) {
        const params = {
          category: type,
          projectId: this.projectID,
          includeCustom: this.attr(`include-custom-properties`) !== `none`,
          workspaceId: this.workspaceId,
        };
        PropertyDefinitionStoreMap[type].fetch(params);
      }
    }

    /**
     * TODO for perf purposes, infra team does not want standard people props list available to filter when a
     * B2B data group has been selected for the main analysis. Hopefully we can remove this restriction
     * eventually, as there are legit analysis cases that it enables.
     */
    private shouldShowUserProps() {
      return this.attr(`project-has-people`) && !this.hasSelectedDataGroup();
    }

    private hasSelectedDataGroup() {
      return this.attr(`all-data-groups`) && !isEmpty(this.attr(`data-group-id`));
    }

    private shouldShowCohorts() {
      return (
        this.attr(`project-has-cohorts`) &&
        (!this.hasSelectedDataGroup() || this.attr(`support-inline-cohorts`) || this.attr(`support-b2b-cohorts`))
      );
    }

    private showItemDetails(item) {
      let tooltipData = null;
      const propertyDefinitions = this.getPropertyResourceTypes().reduce((acc, type) => {
        const definitions = PropertyDefinitionStoreMap[type].value;
        let customDefinitions = definitions
          .filter((definition) => definition.status === `custom`)
          // exclude deleted custom properties
          .filter((definition) => definition.customPropertyId in customPropertiesStore.value)
          // update from update-to-date custom property store, but keep other
          // information that custom property store doesn't have like query counts
          .map((definition) => {
            const customProperty = customPropertiesStore.value[definition.customPropertyId];
            return {
              ...definition,
              description: customProperty.description || definition.description,
              name: customProperty.name || definition.name,
            };
          });

        const inlineCustomPropertyIds = Object.keys(customPropertiesStore.value).filter((customPropertyId) => {
          return (
            !isPersistedCustomProperty(customPropertyId) &&
            !customPropertiesStore.value[customPropertyId].newCustomPropertyId
          );
        });

        const createdCustomPropertyIds = Object.keys(customPropertiesStore.value).filter(
          (customPropertyId) =>
            isPersistedCustomProperty(customPropertyId) &&
            !definitions.find((definition) => definition.customPropertyId === customPropertyId),
        );

        // add in definitions from newly created and inline custom properties
        customDefinitions = customDefinitions.concat(
          [...createdCustomPropertyIds, ...inlineCustomPropertyIds]
            .filter((customPropertyId) => {
              const customProperty = customPropertiesStore.value[customPropertyId];
              return INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING[customProperty.resourceType] === type;
            })
            .map((customPropertyId) => {
              const customProperty = customPropertiesStore.value[customPropertyId];
              const {description, name, resourceType, propertyType} = customProperty;
              return {
                customPropertyId,
                customProperty,
                description,
                name,
                resourceType,
                status: `custom`,
                type: propertyType,
              } as PropertyDefinition;
            }),
        );

        const regularDefinitions = definitions.filter((definition) => definition.status !== `custom`);

        acc[type] = [...customDefinitions, ...regularDefinitions];

        return acc;
      }, {});

      const isCohort = item && item.resourceType === ResourceType.Cohort;

      // show context panel data when data definitions are ready
      if (item.tooltipData) {
        tooltipData = item.tooltipData;
      } else if (item && (isCohort || propertyDefinitions.hasOwnProperty(item.resourceType))) {
        const definitions = propertyDefinitions[item.resourceType];
        tooltipData = isCohort
          ? getTooltipDataForCohort(item)
          : getTooltipDataForProperty(item, definitions, !this.attr(`hide-edit-controls`));
      }

      this.update({tooltipData});
    }

    private getRecentlyViewedSection(resourceTypes, sections, limitPerKey = null) {
      const allSectionsItems = sections.reduce((a, b) => [...a, ...(b.items || [])], []);

      return {
        label: `Recently viewed`,
        items: resourceTypes.reduce((items, resourceType) => {
          const recentItems = this.getRecentItems(resourceType, limitPerKey)
            .map((persistedItem) => {
              const primaryKey = PropertyItemPersistedIdentifierKeys.find((key) => !!persistedItem[key]);
              const propertyItem = allSectionsItems.find((item) => item[primaryKey] === persistedItem[primaryKey]);
              if (!propertyItem) {
                return null;
              }

              return propertyItem;
            })
            .filter(Boolean);

          return [...items, ...recentItems];
        }, []),
      };
    }

    private getMenuSectionWithRecentlyViewed(resourceTypes: ResourceType[], sections, limitPerKey = null) {
      return [
        this.getRecentlyViewedSection(resourceTypes, sections, limitPerKey),
        {
          isLoading: this.state.loadingEventProperties,
          items: this.getPropertyMenuItems({
            properties: this.getCustomFormulas([ResourceType.Event, ResourceType.User]),
          }),
          label: `Formulas in this report`,
          showLimit: MENU_ITEM_LIMIT,
        },
        ...sections,
      ];
    }

    /**
     * Returns all sections to be rendered in the selection menu
     * based on the attribute configuration
     */
    private getMenuSections() {
      const {selectedTab} = this.state;
      const projectHasDataGroups = this.attr(`all-data-groups`);
      const projectHasEvents = this.attr(`project-has-events`);
      const hasOtherOptions = this.attr(`other-options`).length > 0;

      switch (selectedTab) {
        case DefaultToggleTab:
          return this.getMenuSectionWithRecentlyViewed(
            [ResourceType.Event, ResourceType.User, ResourceType.Other],
            [
              ...(hasOtherOptions ? [this.getOtherMenuSection()] : []),
              ...(projectHasEvents ? this.getEventsMenuSections() : []),
              ...(this.shouldShowUserProps() ? this.getUserMenuSections() : []),
              ...(projectHasDataGroups ? this.getDataGroupsSections() : []),
              ...(this.shouldShowCohorts() ? this.getCohortsMenuSections() : []),
            ],
            2,
          );
        case ResourceType.Event:
          return projectHasEvents
            ? this.getMenuSectionWithRecentlyViewed([ResourceType.Event], this.getEventsMenuSections())
            : [];
        case ResourceType.User:
          return this.shouldShowUserProps()
            ? this.getMenuSectionWithRecentlyViewed([ResourceType.User], this.getUserMenuSections())
            : [];
        case ResourceType.Cohort:
          return this.shouldShowCohorts() ? this.getCohortsMenuSections() : [];
        default:
          return projectHasDataGroups ? this.getDataGroupsSections(selectedTab) : [];
      }
    }

    private getEventProperties() {
      if (this._eventPropertySource === EventPropertySource.Store) {
        return eventPropertiesStore.value[this._eventPropertiesStoreKey] || [];
      } else {
        return this.state.eventProperties;
      }
    }

    private getEventsMenuSections() {
      let properties = this.getEventProperties();
      let specialEventProperties = [];
      if (
        this.attr(`include-custom-properties`) !== `none` &&
        this._eventPropertySource !== EventPropertySource.Empty
      ) {
        properties = [...properties, ...this.getCustomProperties(ResourceType.Event)].sort(
          baseComparator({transform: (item) => item.label.toLowerCase()}),
        );
        if (!this.attr(`hide-edit-controls`)) {
          specialEventProperties = [...specialEventProperties, SPECIAL_CONTROLS.CREATE_CUSTOM_EVENT_FORMULA];
        }
      }

      if (this.attr(`show-special-date-property`)) {
        specialEventProperties = [
          ...specialEventProperties,
          {
            label: `Date`,
            icon: `star-top-events`,
            name: `$date`,
            value: `$date`,
            propertyDefaultType: PropertyType.Datetime,
            type: PropertyType.Datetime,
            resourceType: ResourceType.Event,
            isControlItem: true,
          },
        ];
      }

      return [
        {
          isLoading: this.state.loadingEventProperties,
          items: [
            ...specialEventProperties,
            ...this.getPropertyMenuItems({properties, resourceType: ResourceType.Event}),
          ],
          label: `Event properties`,
          showLimit: MENU_ITEM_LIMIT,
        },
      ];
    }

    private isVisibleCustomPropertyForEvents(customProperty): boolean {
      const eventNames = this.eventNames();
      if (this.includeAllProperties(eventNames)) {
        return true;
      }

      const eventProperties = new Set(this.getEventProperties().map((prop) => prop.name));
      if (isPersistedCustomProperty(customProperty.customPropertyId)) {
        // persisted custom properties know from the backend response all the transitively
        // referenced raw event properties
        return Object.values(customProperty.referencedRawEventProperties).some((property: string) =>
          eventProperties.has(property),
        );
      } else {
        // for non-persisted custom properties we need to compute the transitively referenced
        // raw event properties by combining the referenced properties of any nested custom events
        // and any properties referenced directly by this property
        return Object.values(customProperty.composedProperties).some((composedProperty: any) => {
          if (composedProperty.customPropertyId) {
            return customPropertiesStore.value[
              composedProperty.customPropertyId
            ].referencedRawEventProperties.some((property: string) => eventProperties.has(property));
          } else {
            return composedProperty.resourceType === `event` && eventProperties.has(composedProperty.value);
          }
        });
      }
    }

    private isVisibleCustomProperty(customPropertyId: string): boolean {
      if (!isPersistedCustomProperty(customPropertyId)) {
        return true;
      }
      const customProperty = customPropertiesStore.value[customPropertyId];
      if (
        customProperty.resourceType === ResourceType.Event &&
        !this.isVisibleCustomPropertyForEvents(customProperty)
      ) {
        return false;
      }
      return customProperty.isVisible || customProperty.user === this.attr(`user-id`);
    }

    private getCustomEntities(resourceTypes: ResourceType[]) {
      return Object.keys(customPropertiesStore.value)
        .filter((customPropertyId) => !customPropertiesStore.value[customPropertyId].newCustomPropertyId)
        .filter(
          (customPropertyId) =>
            this.attr(`include-custom-properties`) === `all` || isPersistedCustomProperty(customPropertyId),
        )
        .filter((customPropertyId) => this.isVisibleCustomProperty(customPropertyId))
        .map((customPropertyId) => {
          const property = storeCustomPropertyToProperty(customPropertyId);

          return this.getPropertyMenuItem({
            property,
            resourceType: property.resourceType,
          });
        })
        .filter((item) => resourceTypes.includes(item.resourceType));
    }

    private getCustomProperties(resourceType: ResourceType) {
      return this.getCustomEntities([resourceType]).filter((item) => isPersistedCustomProperty(item.customPropertyId));
    }

    private getCustomFormulas(resourceTypes: ResourceType[]) {
      return this.getCustomEntities(resourceTypes).filter((item) => !isPersistedCustomProperty(item.customPropertyId));
    }

    private getUserMenuSections() {
      let properties = this.shouldShowUserProps() ? peoplePropertiesStore.value : [];
      if (this.attr(`include-custom-properties`) !== `none`) {
        properties = [...properties, ...this.getCustomProperties(ResourceType.User)].sort(
          baseComparator({transform: (item) => item.label.toLowerCase()}),
        );
        if (!this.attr(`hide-edit-controls`)) {
          properties = [SPECIAL_CONTROLS.CREATE_CUSTOM_PEOPLE_FORMULA, ...properties];
        }
      }

      return [
        {
          isLoading: this.state.loadingPeopleProperties,
          items: this.getPropertyMenuItems({properties, resourceType: ResourceType.User}),
          label: `Profile properties`,
          showLimit: MENU_ITEM_LIMIT,
        },
      ];
    }

    private isYourCohort(cohort) {
      return cohort.createdBy && cohort.createdBy.id === this.attr(`user-id`);
    }

    private getCohortsMenuSections() {
      const cohorts = sortBy(cohortStore.value, `name`)
        .filter((cohort: SerializedCohort | SerializedInlineCohort) => {
          const dataGroupId = this.attr(`data-group-id`);
          const cohortDataGroupId = isSerializedInlineCohort(cohort)
            ? cohort.raw_cohort.data_group_id
            : cohort.data_group_id;
          return dataGroupIDsEqual(dataGroupId, cohortDataGroupId ? cohortDataGroupId.toString() : ``);
        })
        .map((cohort: SerializedCohort | SerializedInlineCohort) => {
          if (isSerializedInlineCohort(cohort as SerializedInlineCohort)) {
            const unsavedCohort = cohort as SerializedInlineCohort;

            // Hide converted cohorts if the saved cohort exists in store
            if (
              unsavedCohort.raw_cohort.unsavedId &&
              Number.isInteger(unsavedCohort.id) &&
              cohortStore.value[unsavedCohort.id]
            ) {
              return null;
            }

            return {
              label: unsavedCohort.name,
              name: unsavedCohort.name,
              value: {
                cohort: {
                  raw_cohort: unsavedCohort.raw_cohort,
                },
              },
              createdBy: unsavedCohort.created_by,
            };
          }

          const savedCohort = cohort as SerializedCohort;

          return {
            label: savedCohort.name,
            value: savedCohort.id,
            name: savedCohort.name,
            count: savedCohort.count,
            description: savedCohort.description,
            dataGroupId: savedCohort.data_group_id,
            createdBy: savedCohort.created_by,
            lastEdited: savedCohort.last_edited,
            hidden: !savedCohort.is_visible,
            locked: savedCohort.is_locked,
          };
        })
        .filter(Boolean);

      let inlineCohorts;
      let [userCohorts, allCohorts] = partition(cohorts, (cohort) => this.isYourCohort(cohort));
      // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
      [inlineCohorts, userCohorts] = partition(
        this.getPropertyMenuItems({
          properties: userCohorts,
          resourceType: ResourceType.Cohort,
        }),
        (cohort) => cohort.value && cohort.value.cohort && cohort.value.cohort.raw_cohort,
      );

      // @ts-ignore - TODO: Property 'hidden' does not exist on type 'Cohort'.
      allCohorts = allCohorts.filter((cohort) => !cohort.hidden);
      // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
      allCohorts = this.getPropertyMenuItems({
        properties: allCohorts,
        resourceType: ResourceType.Cohort,
      });

      // defines cohorts that were last selected and currently selected
      const currentlySelected = intersectionBy(this.attr(`selected`), this.state.lastMultiSelection, `value`);
      const selectedCohorts = this.getPropertyMenuItems({
        properties: cohorts,
        resourceType: ResourceType.Cohort,
        selected: currentlySelected,
      }).filter((cohort) => cohort.isSelected);

      if (this.attr(`show-all-users-cohort`)) {
        // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
        allCohorts.unshift(ALL_USERS_COHORT);
      }

      const sections = [
        {
          isLoading: this.state.loadingCohorts,
          items: selectedCohorts,
          label: `Selected Cohorts`,
        },
        {
          isLoading: this.state.loadingCohorts,
          items: [
            ...(this.attr(`support-inline-cohorts`) ? [SPECIAL_CONTROLS.CREATE_INLINE_COHORT] : []),
            ...inlineCohorts,
            ...userCohorts,
          ],
          label: `Your Cohorts`,
        },
        {
          isLoading: this.state.loadingCohorts,
          items: allCohorts,
          label: `All Cohorts`,
          showLimit: userCohorts.length > 0 ? MENU_ITEM_LIMIT : null,
        },
      ];

      return sections;
    }

    private getOtherMenuSection() {
      const items: PropertyItem[] = this.attr(`other-options`)
        .reduce((r, opt) => (OTHER_OPTIONS.has(opt) ? r.concat(OTHER_OPTIONS.get(opt)) : r), [])
        .map((item) => (this.isItemSelected(item, this.attr(`selected`)) ? {...item, isSelected: true} : item));
      return {
        isLoading: false,
        items,
        label: `Other`,
      };
    }

    /**
     * Generates items-menu sections from each loaded data group
     */
    private getDataGroupsSections(dataGroupId?: string) {
      return this.getVisibleDataGroups(dataGroupId).map((group) => {
        return {
          isLoading: group.isLoading,
          items: this.getPropertyMenuItems({
            dataGroupId: group.groupID,
            properties: group.properties,
            resourceType: ResourceType.User,
          }),
          label: `${group.displayName} Properties`,
          showLimit: MENU_ITEM_LIMIT,
        };
      });
    }

    private getVisibleDataGroups(dataGroupId?: string) {
      const {dataGroups} = this.state;
      const singleDataGroup = this.attr(`single-data-group`);
      let groups = dataGroupId ? (dataGroups[dataGroupId] ? [dataGroups[dataGroupId]] : []) : Object.values(dataGroups);

      if (singleDataGroup) {
        groups = groups.filter((group) => group.groupID === singleDataGroup);
      } else {
        // Dimensions are not part of visible groups
        groups = groups.filter((group) => !group.isDimension);
      }

      return groups;
    }

    /**
     * Generates all tabs to be displayed in the selection menu
     */
    private getToggleTabs() {
      const projectHasEvents = this.attr(`project-has-events`);
      const showAllTab = this.attr(`show-all-tab`);

      let tabs = [
        ...(projectHasEvents ? [{value: ResourceType.Event, label: `Event`, disabled: false}] : []),
        ...(this.shouldShowUserProps() ? [{value: ResourceType.User, label: `User`, disabled: false}] : []),
        ...this.getVisibleDataGroups().map((group) => ({
          label: group.displayName,
          value: group.groupID,
          disabled: false,
        })),
        ...(this.shouldShowCohorts() ? [{value: ResourceType.Cohort, label: `Cohort`, disabled: false}] : []),
      ];

      if (showAllTab && tabs.length > 1) {
        tabs = [{value: DefaultToggleTab, label: `All`, disabled: false}, ...tabs];
      }

      return tabs;
    }

    private eventNames(): string[] {
      return this.attr(`event-names`) && uniq<string>(this.attr(`event-names`));
    }

    private includeAllProperties(eventNames: string[]): boolean {
      // if no events are passed, or events like `$all_events` are passed, all properties should be fetched
      return (
        !eventNames ||
        eventNames.length === 0 ||
        eventNames.some((eventName) => {
          return [SPECIAL_EVENTS.ALL_EVENTS, SPECIAL_EVENTS.ANY_EVENT, SPECIAL_EVENTS.TOP_EVENTS].includes(eventName);
        })
      );
    }

    /**
     * Fetches event properties for a set of events or all events
     */
    private async fetchEventProperties() {
      const eventNames = this.eventNames();
      const isPropertyIntersection = this.attr(`property-combine-mode`) === PropertyCombineMode.Intersection;
      const includeAllProperties = this.includeAllProperties(eventNames);

      this.update({loadingEventProperties: true});
      this._eventPropertiesStoreCallback = () => {
        this.update({loadingEventProperties: false});
      };

      let eventProperties = null;
      let params = null;
      let eventPropertySource;
      this._eventPropertiesStoreKey = ``;
      if (!this.attr(`project-has-events`) || (eventNames && eventNames.length === 0)) {
        eventProperties = [];
        eventPropertySource = EventPropertySource.Empty;
      } else if (!includeAllProperties && isPropertyIntersection) {
        const requestParamsList = eventNames.map((eventName) => ({
          eventName,
          projectId: this.projectID,
          workspaceId: this.workspaceId,
        }));
        eventProperties = await Promise.all(
          requestParamsList.map((requestParams) => eventPropertiesClient.query(requestParams)),
        );
        eventProperties = calculatePropertiesIntersection(eventProperties);
        eventPropertySource = EventPropertySource.Intersection;
      } else {
        params = {
          ...(includeAllProperties ? {} : {eventName: eventNames}),
          includeDimensionProperties: !this.attr(`exclude-dimension-properties`),
          projectId: this.attr(`project-id`),
          workspaceId: this.attr(`workspace-id`),
        };
        this._eventPropertiesStoreKey = Store.getKey(params);
        eventPropertySource = EventPropertySource.Store;
      }

      eventPropertiesStore.subscribe(this._eventPropertiesStoreCallback, this._eventPropertiesStoreKey);

      if (eventProperties) {
        this.update({eventProperties, loadingEventProperties: false});
      } else {
        eventPropertiesStore.fetch(params, {}, this._eventPropertiesStoreKey);
      }
      this._eventPropertySource = eventPropertySource;
    }

    private fetchPeopleProperties() {
      this.update({loadingPeopleProperties: true});
      this._peoplePropertiesStoreCallback = () => {
        this.update({loadingPeopleProperties: false});
      };
      peoplePropertiesStore.subscribe(this._peoplePropertiesStoreCallback);
      peoplePropertiesStore.fetch({
        includeDimensionProperties: !this.attr(`exclude-dimension-properties`),
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
      });
    }

    /**
     * Updates a given dataGroup with its list of properties
     */
    private async fetchDataGroupResourceProperties(
      resourceType: ResourceType,
      dataGroupId: string,
      dimension?: [string],
    ) {
      let properties = [];
      const dataGroup = this.state.dataGroups[dataGroupId];
      const params = {
        dataGroupId,
        projectId: this.projectID,
        ...(dimension
          ? {
              includeDimensionProperties: true,
            }
          : {}),
        workspaceId: this.workspaceId,
      };

      if (!dataGroup) {
        this.update({
          dataGroups: {
            ...this.state.dataGroups,
            [dataGroupId]: {
              isLoading: true,
              properties: [],
            } as DataGroupItem,
          },
        });
      }

      try {
        if (resourceType === ResourceType.User) {
          properties = await peoplePropertiesClient.query(params);
        } else if (resourceType === ResourceType.Event) {
          properties = await eventPropertiesClient.query(params);
        } else {
          throw new Error(`Failed to fetch properties for invalid resource type ${resourceType}`);
        }

        if (dimension) {
          properties = properties.map((property) => ({
            ...property,
            value: [...dimension, property.name],
          }));
        }
      } catch (error) {
        console.error(`Error loading data group (${dataGroupId}) user properties`, error);
      }

      this.update({
        dataGroups: {
          ...this.state.dataGroups,
          [dataGroupId]: {
            ...dataGroup,
            isLoading: false,
            isDimension: !!dimension,
            properties,
          },
        },
      });
    }

    /**
     * Fetches all data groups and triggers their
     * respective properties request
     */
    private async fetchDataGroups() {
      if (!this.attr(`all-data-groups`)) {
        return this.update({dataGroups: {}});
      }
      const dataGroups = {};

      const groups = await this.retrieveDataGroups();

      for (const group of groups) {
        const dataGroupId = group.groupID;
        const cachedProperties = peoplePropertiesClient.getCachedResponse({
          dataGroupId,
          projectId: this.projectID,
          workspaceId: this.workspaceId,
        });

        dataGroups[group.groupID] = {
          ...group,
          isLoading: cachedProperties ? false : true,
          properties: cachedProperties || [],
        };
      }

      // only fetch properties for data groups that are not cached
      this.update({dataGroups});
      groups.forEach(({groupID}) => {
        if (dataGroups[groupID].isLoading) {
          this.fetchDataGroupResourceProperties(ResourceType.User, groupID);
        }
      });
    }

    private fetchUserDetails() {
      userDetailsStore.fetch();
    }

    private async fetchCohorts() {
      if (!this.attr(`project-has-cohorts`)) {
        return;
      }

      this.update({loadingCohorts: true});

      // Handle unsaved -> saved conversion on store change
      this._cohortStoreCallback = () => {
        this.update({loadingCohorts: false});

        const selection = this.getInlineCohortSelection();

        selection.forEach(({value}, index) => {
          if (!value.cohort.raw_cohort.unsavedId) {
            return;
          }

          const unsavedCohort = cohortStore.value[value.cohort.raw_cohort.unsavedId] as SerializedInlineCohort;

          if (!unsavedCohort) {
            return;
          }

          // Inline cohort converted
          if (Number.isInteger(unsavedCohort.id)) {
            const cohort = cohortStore.value[unsavedCohort.id];

            this.replaceItem(
              {
                propertyDefaultType: PropertyType.List,
                resourceType: ResourceType.Cohort,
                label: cohort.name,
                value: {
                  cohort,
                },
                createdBy: cohort.created_by,
              },
              index,
              null,
              false,
              {commit: true},
            );
          } else if (!isEqual(unsavedCohort.raw_cohort, value.cohort.raw_cohort)) {
            // Inline cohort updated
            this.replaceItem(
              {
                propertyDefaultType: PropertyType.List,
                resourceType: ResourceType.Cohort,
                label: unsavedCohort.name,
                value: {
                  cohort: unsavedCohort,
                },
                createdBy: unsavedCohort.created_by,
              },
              index,
              null,
              false,
              {commit: true},
            );
          }
        });
      };

      const dataGroupId = this.attr(`data-group-id`);
      if (dataGroupId && this.attr(`support-b2b-cohorts`)) {
        cohortStore.subscribe(this._cohortStoreCallback, dataGroupId);
        await cohortStore.fetch(
          {
            projectId: this.attr(`project-id`),
            workspaceId: this.attr(`workspace-id`),
            dataGroupId,
          },
          {},
          dataGroupId,
        );
      } else {
        cohortStore.subscribe(this._cohortStoreCallback);
        await cohortStore.fetch({
          projectId: this.attr(`project-id`),
          workspaceId: this.attr(`workspace-id`),
        });
      }
    }

    private getPropertyMenuItems({
      dataGroupId,
      properties,
      resourceType,
      selected,
      propertyDefaultType,
    }: {
      dataGroupId?: string;
      properties?: any[];
      resourceType?: ResourceType;
      selected?: any;
      propertyDefaultType?: PropertyType;
    } = {}): PropertyItem[] {
      let items = properties.map((property) =>
        this.getPropertyMenuItem({dataGroupId, property, resourceType, selected, propertyDefaultType}),
      );

      const filterByTypes = this.attr(`filter-by-types`);
      if (filterByTypes) {
        items = items.filter((property) => property.isControlItem || filterByTypes.includes(property.type));
      }

      return items;
    }

    private getPropertyMenuItem({
      dataGroupId,
      property,
      resourceType,
      selected,
      propertyDefaultType,
    }: {
      dataGroupId?: string;
      property?: any;
      resourceType?: ResourceType;
      selected?: any;
      propertyDefaultType?: PropertyType;
    } = {}): PropertyItem {
      const forCohorts = resourceType === ResourceType.Cohort;
      const allowTypecast = this.attr(`allow-typecast`);
      const canTypecast = !forCohorts && allowTypecast;

      if (property.resourceType) {
        // custom properties/formulas specify their own resource types
        resourceType = property.resourceType;
      }

      selected = selected || this.attr(`selected`);

      const {customPropertyId, customProperty, isControlItem} = property;
      let {label, name} = property;
      const value = property.value || name || (customProperty && customProperty.name);
      label = label || value;
      name = name || value;

      const isSelected =
        (forCohorts && this.isItemSelected(value, selected)) ||
        (selected && dataGroupId && dataGroupId === selected.dataGroupId && selected.value === value) ||
        (selected && !selected.dataGroupId && selected.value === value && selected.resourceType === resourceType) ||
        (selected && selected.customPropertyId && selected.customPropertyId === customPropertyId) ||
        false;
      const appliedDataGroupId = property.dataGroupId ? property.dataGroupId : dataGroupId;

      const type = (isSelected && selected.type) || this.getPropertyType(name, property.type);
      const propertyType = propertyDefaultType || (isSelected && selected.propertyDefaultType) || property.type;
      const isDisabled = this.attr(`disabled-items`).hasOwnProperty(value) || property.sensitive;
      const tooltip = property.sensitive ? {text: DISABLED_DUE_TO_SENSITIVE_DATA} : null;
      const isDimension = type !== PropertyType.Dimension;

      return {
        ...(appliedDataGroupId ? {dataGroupId: appliedDataGroupId} : {}),
        ...(customPropertyId ? {customPropertyId} : {}),
        ...(customProperty ? {customProperty} : {}),
        ...(forCohorts ? property : {}),
        ...(property.items ? {items: property.items} : {}),
        ...(property.tooltipData ? {tooltipData: property.tooltipData} : {}),
        hasCaret: (isDimension || this.attr(`show-items-caret`)) && !isControlItem && !property.sensitive,
        hasPill: isDimension && canTypecast && !isControlItem && !property.sensitive,
        icon: property.icon || getIconForPropertyType(type),
        isControlItem,
        isDisabled,
        tooltip,
        isSelected,
        label,
        name,
        pillAction: PropertyItemAction.Typecast,
        pillLabel: `Typecast`,
        propertyDefaultType: propertyType,
        resourceType,
        type,
        value,
      };
    }

    private getPropertyType(name: string, type: PropertyType): PropertyType {
      return STRING_TYPECAST_PROPERTIES.includes(name) ? PropertyType.String : type;
    }

    private getArraySelection() {
      const selected = this.attr(`selected`);

      if (Array.isArray(selected)) {
        return selected;
      }

      return [];
    }

    private isItemSelected(value, selection: PropertyItem[] | PropertyItem) {
      const getCompareValue = (v) => (v && v.cohort && v.cohort.raw_cohort ? v.cohort.raw_cohort.unsavedId : v);

      if (Array.isArray(selection)) {
        return selection.some((item) => getCompareValue(item.value) === getCompareValue(value));
      }

      return selection && getCompareValue(selection.value) === getCompareValue(value);
    }

    private editCohort(item) {
      const cohort = item.value && item.value.cohort ? item.value.cohort : item.value;

      this.update({
        stagedCohort: CohortModel.parseCohort(cohort.raw_cohort || cohort)
          .toStagedCohort()
          .setUnsavedIdIfNotSet(cohort.raw_cohort.unsavedId),
        openModal: MODAL_TYPE.InlineCohort,
        tooltipData: null,
      });

      this.trackEvent(TrackingEvent.OpenInlineCohortsModal, {
        action: `Edit`,
        context: this._context,
      });
    }

    private createCohort() {
      this.update({
        stagedCohort: new StagedCohort(),
        openModal: MODAL_TYPE.InlineCohort,
      });

      this.trackEvent(TrackingEvent.OpenInlineCohortsModal, {
        action: `Create`,
        context: this._context,
      });
    }

    private commitStagedCohort(
      {
        id,
        name,
      }: {
        id?: number;
        name?: string;
      },
      {isSaved = false} = {},
    ) {
      let cohort;
      let {stagedCohort} = this.state;
      const isTemporary = !id;
      const isEditingCohort = !!stagedCohort.unsavedId;

      if (!isEditingCohort) {
        stagedCohort = stagedCohort.setUnsavedIdIfNotSet();
      }

      if (isSaved) {
        cohort = {
          id,
          // Include name if available
          ...(name
            ? {
                name,
              }
            : {}),
        };
      } else {
        const {behaviors, selector, data_group_id} = CohortModel.createCohortFromStaged(stagedCohort).serialize();

        cohort = {
          name,
          raw_cohort: {
            name,
            behaviors,
            selector,
            data_group_id,
            unsavedId: stagedCohort.unsavedId,
            ...(userDetailsStore.value
              ? {
                  createdBy: {
                    id: this.attr(`user-id`),
                    name: userDetailsStore.value.name,
                    email: userDetailsStore.value.email,
                  },
                }
              : {}),
          },
        };
      }
      const item = {
        propertyDefaultType: PropertyType.List,
        resourceType: ResourceType.Cohort,
        label: name,
        value: {
          cohort,
        },
        createdBy: cohort.createdBy,
      } as PropertyItem;

      const selected = this.attr(`selected`);

      // based on internal user feedback cohorts created inline
      // should be commited much like custom props with breakdowns
      if (isEditingCohort && selected) {
        const index = Array.isArray(selected)
          ? this.getArraySelection().findIndex(
              (selectedItem) =>
                selectedItem.value.cohort &&
                selectedItem.value.cohort.raw_cohort &&
                selectedItem.value.cohort.raw_cohort.unsavedId === stagedCohort.unsavedId,
            )
          : isEqual(selected.value, item.value);

        this.replaceItem(item, index, null, false, {commit: true});
      } else {
        this.selectItem(item, null, false, {commit: true});
      }

      if (isTemporary) {
        cohortStore.update({
          [stagedCohort.unsavedId]: {
            name,
            raw_cohort: cohort.raw_cohort,
            created_by: cohort.createdBy,
          } as SerializedInlineCohort,
        });
      } else if (stagedCohort.unsavedId) {
        // Mark unsaved cohort being converted to a saved cohort
        cohortStore.update({
          [stagedCohort.unsavedId]: {
            name,
            created_by: cohort.createdBy,
            ...(cohortStore.value[stagedCohort.unsavedId] || {}),
            id: cohort.id,
          } as SerializedInlineCohort,
        });
      }

      this.update({openModal: null});

      if (isSaved) {
        this.trackEvent(TrackingEvent.SaveInlineCohorts, {
          context: this._context,
          name,
          // Indicates whether an applied inline cohort was later saved
          converted: !!isEditingCohort,
        });
      } else if (!isEditingCohort) {
        this.trackEvent(TrackingEvent.CreateInlineCohort, {
          context: this._context,
          name,
        });
      } else {
        this.trackEvent(TrackingEvent.EditInlineCohorts, {
          context: this._context,
          name,
        });
      }
    }

    private getInlineCohortSelection() {
      const selected = this.attr(`selected`);
      const cohorts = Array.isArray(selected) ? selected : [selected];

      return cohorts.filter((item) => item && item.value && item.value.cohort && item.value.cohort.raw_cohort);
    }

    private getInlineCohortItems() {
      return this.getInlineCohortSelection().map((item) => ({
        ...item,
        createdBy: item.value.cohort.raw_cohort.createdBy,
        resourceType: ResourceType.Cohort,
      }));
    }

    private getSelectedCustomFormula(resourceType) {
      const selected = this.attr(`selected`) ? [this.attr(`selected`)] : [];

      return selected.filter(
        (item) =>
          item.resourceType === resourceType &&
          item.customPropertyId &&
          !isPersistedCustomProperty(item.customPropertyId),
      );
    }

    set useMultipleSelection(value) {
      this._useMultipleSelection = value;
      this.update();
    }

    set context(value) {
      this._context = value;
    }
  },
);
